// What the shared activity page (AgentActivityOverview) says for each agent type.
// An agent's "win" is a sale, or for a review agent a review, which carries no revenue.
const ACTIVITY_CONFIG = {
  AbandonCartAgent: {
    successLabel: 'Sales',
    revenueLabel: 'Recovered Revenue',
    hasRevenue: true,
    showSegment: false,
    emptyText:
      'No abandoned cart conversations in this date range. They appear here once your agent follows up on a cart.'
  },
  WinbackAgent: {
    successLabel: 'Sales',
    revenueLabel: 'Win-back Revenue',
    hasRevenue: true,
    showSegment: true,
    emptyText: 'No win-back conversations in this date range. They appear here once your agent reaches out.'
  },
  ReviewAgent: {
    successLabel: 'Reviews',
    revenueLabel: '',
    hasRevenue: false,
    showSegment: false,
    emptyText: 'No review conversations in this date range. They appear here once your agent checks in on an order.'
  }
}

export const activityConfigFor = agentType => ACTIVITY_CONFIG[agentType] || null

const WON_OUTCOMES = ['converted', 'reactivated', 'reviewed']

const OUTCOME_LABELS = {
  pending: 'In progress'
}

export const outcomeLabel = outcome => {
  if (OUTCOME_LABELS[outcome]) return OUTCOME_LABELS[outcome]
  const words = String(outcome || '').replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export const outcomePillClass = outcome => {
  if (WON_OUTCOMES.includes(outcome)) return 'bg-green-100 text-green-800'
  if (outcome === 'handoff') return 'bg-amber-100 text-amber-800'
  return 'bg-gray-100 text-gray-800'
}

const SEGMENT_LABELS = {
  WinbackSlipping: 'Slipping away',
  WinbackLost: 'Fully lost'
}

export const segmentLabel = segment => SEGMENT_LABELS[segment] || segment || '-'

export const segmentPillClass = segment => {
  if (segment === 'WinbackLost') return 'bg-red-100 text-red-800'
  if (segment === 'WinbackSlipping') return 'bg-amber-100 text-amber-800'
  return 'bg-gray-100 text-gray-800'
}

export const conversationLink = row => {
  const cup = row.attributes.conversation_user_pair_id
  const msg = row.attributes.first_outbound_message_id
  return msg ? `/messages/${cup}?messageId=${msg}` : `/messages/${cup}`
}

const EMPTY_DAY = { conversations: 0, replied: 0, successes: 0, revenue_cents: 0 }

// One row per day of the range, so quiet days show as gaps in the chart rather
// than being skipped.
export const dailySeries = (periods, { beginning_date, ending_date }) => {
  const byDate = new Map(periods.map(period => [period.date, period]))
  const days = []
  const end = new Date(ending_date)
  for (let day = new Date(beginning_date); day <= end; day.setDate(day.getDate() + 1)) {
    const iso = day.toISOString().slice(0, 10)
    days.push(byDate.get(iso) || { date: iso, ...EMPTY_DAY })
  }
  return days
}

export const rate = (part, whole) => (whole ? part / whole : null)

export const ratio = (current, prior) => (prior ? (current - prior) / prior : null)

export const formatDollars = cents => {
  const dollars = (cents || 0) / 100
  return dollars === 0 ? '-' : `$${dollars.toFixed(2)}`
}

export const formatDateTime = iso => (iso ? new Date(iso).toLocaleString() : '-')

const customersMatch = count => (count === 1 ? '1 customer matches' : `${count} customers match`)

// One plain sentence from the impact estimate of a saved agent: who matches
// now and how the daily cap spreads them. Nothing is queued on the server;
// whoever does not fit today is picked up by the next day's run.
export const queueSentence = estimate => {
  if (!estimate) return null
  const eligible = Number(estimate.eligible_count) || 0
  const remaining = Number(estimate.remaining_today) || 0
  if (eligible === 0) return 'No customers match right now, so nobody is waiting.'
  const lead = `${customersMatch(eligible)} right now.`
  if (remaining === 0) return `${lead} Today's limit is used up, so they go out from tomorrow.`
  if (eligible <= remaining) return `${lead} All of them go out today.`
  return `${lead} ${remaining} go out today and ${eligible - remaining} are waiting for the next days.`
}
